import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Group,
  Center,
  Stack,
  Text,
  Container,
  rem,
  LoadingOverlay,
  Button,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Lottie from "react-lottie-player";
import Microblink from "./microblink/capture/Index";
import VeratadCapture from "./veratad/capture";
import FaceCapture from "../face-capture";
import { useParams } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import InstructionsCarousel from "./instructions-carousel";
import { Carousel } from "@mantine/carousel";
import Mitek from "./mitek/capture";
import { isMobile } from "react-device-detect";
import Handoff from "../handoff";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { IconSunHigh, IconId, IconCapture } from "@tabler/icons-react";
import CenterLoadingOverlay from "../../components/center-loading-overlay";
import EnglishLoadingWithCompletion from "../../components/english-loading-with-completion";
import IDScan from "../../assets/id-scan-loading.json";
import VerificationLoadingScreen from "../../components/verification-loading-screen";
import StepperLoader from "../../components/stepper-loader";
export default function Dcams({ isModular = false }) {
  const { sdk } = useParams();
  const { data, settings, setSessionState, text } = useContext(DataContext);
  const theme = useMantineTheme();
  const [isSDKActive, setIsSDKActive] = useState(false);
  const [isSDKComplete, setIsSDKComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAnimationComplete, setUploadAnimationComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [getFace, setGetFace] = useState(false);
  const [gotFace, setGotFace] = useState(false);
  const [status, setStatus] = useState("");
  const [onMobile, setOnMobile] = useState(isMobile);
  
  console.log('📸 Dcams component state:', {
    isUploading,
    uploadAnimationComplete,
    isSDKComplete,
    isSDKActive,
    loading
  });

  useEffect(() => {
    if (data?.isReactNative) {
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  // Set SDK complete only after upload animation finishes
  useEffect(() => {
    if (uploadAnimationComplete && !isSDKComplete) {
      console.log('🎯 Setting SDK complete after animation');
      setIsSDKComplete(true);
      setUploadAnimationComplete(false);
    }
  }, [uploadAnimationComplete, isSDKComplete]);

  const cp = isModular ? "dcams/modular" : "dcams/mitek";
  const isSmallMobile = useMediaQuery("(max-width: 395px)");

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode && isSDKComplete, false, cp);
  useEdgeStatusCheck(isEdgeMode && isSDKComplete);

  useEffect(() => {
    if (isSDKComplete) {
      if (settings.journey.settings?.isSelfie && !gotFace) {
        setIsUploading(false);
        setIsSDKComplete(false);
        setGetFace(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      data.settings = settings;
      data.type = "SDK_Scan";
      
      // Prepare request data and URL
      let requestData = { ...data };
      let apiUrl;
      
      // Check if edge mode is enabled
      if (data?.edge === true) {
        apiUrl = process.env.REACT_APP_EDGE_QUEUE_URL;
        // Add queue parameter for veratadai
        requestData.queue = "veratadai";
      } else {
        apiUrl = "https://vx.dcams.app/frontend/dcams/process";
      }
      
      fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (result) {
          setLoading(false);
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
        });
    }
  }, [isSDKComplete, gotFace]);

  const handleFaceCaptureComplete = () => {
    setIsSDKComplete(true);
    setGotFace(true);
  };

  if (isSDKComplete) {
    // Use new StepperLoader only for Veratad sites
    if (settings?.site_name?.toLowerCase().includes('veratad')) {
      const verificationSteps = [
        {
          title: "Document Analysis",
          description: "Analyzing document structure and format"
        },
        {
          title: "Security Check",
          description: "Verifying security features and authenticity"
        },
        {
          title: "Data Extraction",
          description: "Extracting and processing personal information"
        },
        {
          title: "Final Verification",
          description: "Cross-referencing data with secure databases"
        }
      ];

      return (
        <CenterLoadingOverlay
          content={
            <StepperLoader
              title="Verifying Identity Document"
              steps={verificationSteps}
              stepDuration={1500}
              onComplete={() => {
                // Verification complete - status check will handle navigation
              }}
            />
          }
        />
      );
    }

    // Use original loader for all other sites
    return (
      <CenterLoadingOverlay
        content={
          <VerificationLoadingScreen type="documentVerificationLongDuration" />
        }
      />
    );
  }

  const handleStart = () => {
    setIsSDKActive(true);
  };

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  if (isUploading) {
    const uploadMessages = [
      "Processing your document...",
      "Extracting information...",
      "Uploading to secure server...",
      "Verifying document quality...",
      "Finalizing upload..."
    ];

    if (isEnglish) {
      return (
        <EnglishLoadingWithCompletion 
          messages={uploadMessages}
          onComplete={() => {
            console.log('🏁 Document upload animation completed');
            setIsUploading(false);
            setUploadAnimationComplete(true);
          }}
        />
      );
    } else {
      // For non-English, immediately mark as complete
      setTimeout(() => {
        setIsUploading(false);
        setUploadAnimationComplete(true);
      }, 3000); // Give some time for the loading
      return <LoadingOverlay visible />;
    }
  }

  if (loading) {
    return <LoadingOverlay visible />;
  }

  // We now use the onMobile state defined above
  
  if (!onMobile) {
    // Check if shouldShowContinueOnDesktop is explicitly set to true in settings
    // Default to false if setting doesn't exist
    const should_show_continue_on_desktop = settings?.shouldShowContinueOnDesktop === true;
    
    // Create the HandoffComponent with or without the continue button based on settings
    const HandoffComponent = should_show_continue_on_desktop ? (
      <>
        <Handoff nextPath="dcams/mitek" />
        <Button onClick={() => setOnMobile(true)} mt='sm' fullWidth variant="subtle">
          {text.qr?.troublePrimaryButton || "Continue on this device"}
        </Button>
      </>
    ) : <Handoff nextPath="dcams/mitek" />;
    
    // Return the handoff component
    return HandoffComponent;
  }

  const instructions = [
    { Icon: IconSunHigh, instruction: text.dcams.instructions[0] },
    { Icon: IconId, instruction: text.dcams.instructions[1] },
    { Icon: IconCapture, instruction: text.dcams.instructions[2] },
  ];

  return getFace ? (
    <FaceCapture onCompletion={handleFaceCaptureComplete} />
  ) : (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box>
          <Stack align="center" spacing="md">
            <ThemeIcon
              variant="light"
              c={data.styles?.primaryColor ? data.styles.primaryColor : "teal"}
              size={85}
              radius={"xl"}
            >
              <IconId size={70} stroke={1} />
            </ThemeIcon>

            <Text fz={30} fw={700} align="center">
              {text.dcams.title}
            </Text>
            <Text align="center">{text.dcams.subTitle}</Text>
            <Paper
              withBorder
              p="md"
              style={{ 
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa", 
                width: "100%" 
              }}
            >
              <Stack spacing={isSmallMobile ? "xs" : "lg"}>
                {instructions.map(({ Icon, instruction }, index) => (
                  <Group
                    key={index}
                    spacing={isSmallMobile ? "xs" : "sm"} // Adjust spacing for very small screens
                    noWrap
                  >
                    {/* Render the icon dynamically with the size prop */}
                    <Icon size={isSmallMobile ? 16 : 30} />

                    {/* Text with responsive font size */}
                    <Text
                      fz={isSmallMobile ? "12px" : "20px"} // Smaller font size for very small screens
                      
                    >
                      {instruction}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Box>
        <Box>
          <Text fz="sm" color="dimmed" align="center" pb={15}>
            {text.dcams.note}
          </Text>

          <Microblink
            onUpload={() => {
              console.log('📤 Dcams: onUpload callback triggered');
              setIsUploading(true);
            }}
            onComplete={() => {
              console.log('✅ Dcams: onComplete callback triggered');
              // Don't set SDK complete here - wait for animation to finish
              // The animation onComplete will trigger setUploadAnimationComplete
              // which will then set isSDKComplete via useEffect
            }}
            onStart={handleStart}
          />
        </Box>
      </Box>
    </>
  );
}
